import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { isConflictError } from '@discovr/common';
import type {
  DiscoveryChatPatch,
  DiscoveryPersona,
  DiscoveryPersonaChat,
  DiscoveryPersonaWrite,
  DiscoveryVettingPolicy,
} from '@discovr/types';
import { discoveryApi } from '../api/discoveryApi';
import { discoveryKeys } from '../queryKeys';

/** A 409 means the campaign moved under the call, so what the screen shows is stale. */
const refreshOnConflict =
  (queryClient: QueryClient, campaignId: string, { chats = false } = {}) =>
  (error: unknown) =>
    isConflictError(error)
      ? Promise.all([
          queryClient.invalidateQueries({ queryKey: discoveryKeys.campaign(campaignId) }),
          chats && queryClient.invalidateQueries({ queryKey: discoveryKeys.chats(campaignId) }),
        ])
      : undefined;

/** A save against a stale `castVersion` gets a 409; the campaign is then read again before the error reaches the caller. */
export function useSavePersonas(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      personas,
      castVersion,
    }: {
      personas: DiscoveryPersona[];
      castVersion: string;
    }) => discoveryApi.patch(campaignId, { personas, castVersion }),
    onSuccess: (detail) => queryClient.setQueryData(discoveryKeys.campaign(campaignId), detail),
    onError: () => queryClient.invalidateQueries({ queryKey: discoveryKeys.campaign(campaignId) }),
  });
}

export function useSaveVetting(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vetting: DiscoveryVettingPolicy) => discoveryApi.patch(campaignId, { vetting }),
    onSuccess: (detail) => queryClient.setQueryData(discoveryKeys.campaign(campaignId), detail),
    onError: () => queryClient.invalidateQueries({ queryKey: discoveryKeys.campaign(campaignId) }),
  });
}

export function useGeneratePersonas(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (count: number) => discoveryApi.generatePersonas(campaignId, count),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: discoveryKeys.campaign(campaignId) }),
    onError: refreshOnConflict(queryClient, campaignId),
  });
}

export function useEnsurePersonaSearch(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (personaIds: string[]) => discoveryApi.ensurePersonaSearch(campaignId, personaIds),
    onSuccess: (detail) => queryClient.setQueryData(discoveryKeys.campaign(campaignId), detail),
  });
}

export function usePreviewPersonaSearch(campaignId: string) {
  return useMutation({
    mutationFn: (persona: DiscoveryPersona) =>
      discoveryApi.previewPersonaSearch(campaignId, persona),
  });
}

export function usePatchChat(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ chatId, ...body }: DiscoveryChatPatch & { chatId: string }) =>
      discoveryApi.patchChat(campaignId, chatId, body),
    onSuccess: (_summary, { personaId }) => {
      if (personaId)
        void queryClient.invalidateQueries({ queryKey: discoveryKeys.chats(campaignId) });
    },
  });
}

export function useDropChat(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (chatId: string) => discoveryApi.dropChat(campaignId, chatId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: discoveryKeys.chats(campaignId) }),
  });
}

export function usePersonaWrite(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DiscoveryPersonaWrite) => discoveryApi.writePersona(campaignId, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: discoveryKeys.chats(campaignId) }),
    onError: refreshOnConflict(queryClient, campaignId, { chats: true }),
  });
}

export function usePersonaChat(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DiscoveryPersonaChat) => discoveryApi.chatPersona(campaignId, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: discoveryKeys.chats(campaignId) }),
    onError: refreshOnConflict(queryClient, campaignId, { chats: true }),
  });
}

export function useCheckPersonaQuestion(campaignId: string) {
  return useMutation({
    mutationFn: (question: string) => discoveryApi.checkPersonaQuestion(campaignId, question),
  });
}
